// sim/colonyAI.ts — the DECISION ENGINE: high-level colony autonomy.
//
// The colony runs ITSELF with no player input. Each colony, on a staggered
// cadence (tick % decisionInterval === cid % decisionInterval, so colonies don't
// all decide on the same tick), does three things:
//
//   1. ANALYZE NEEDS — fill a per-colony integer need vector (NEED.*):
//        Food         rises as the food store falls below lowFood vs population;
//        Defense      rises with rival organisms scanned near the nest, minus the
//                     soldiers already on hand;
//        Expansion    rises when population outgrows the territory the current
//                     hive nodes support (gated off once at the hive cap);
//        Reproduction the resting/growth need: headroom under maxPopulation,
//                     scaled by available food;
//        Evolution    rises with genes held beyond evolutionMinGenes.
//
//   2. ARBITRATE — pick ONE winning action by FIXED PRIORITY:
//        Food > Defense > Expansion > Evolution, taking the first whose score
//        clears its threshold; if none clear, the fallback is Reproduction (rest
//        + grow). The winner is recorded in `priority[cid]` (debug/hash/CLI).
//
//   3. ACT — drive the world toward the winner. Every action is SELF-CORRECTING:
//        it only nudges spawn bias + enqueues a few tasks, and the whole decision
//        is recomputed next cadence, so when the threat clears or the larder
//        fills the colony naturally reverts. Expansion is multi-step and
//        autonomous: Scout a candidate → on arrival promote to Expand → on
//        arrival FOUND a hive node (record it, pay the colony a food dividend).
//
// DETERMINISM: pure integer state (exact FNV hashing), fixed colony order, RNG
//   only from ctx. Allocation-free — decisions run on a cadence but never alloc.

import { CASTE, CHAMBER, NEED, NEED_COUNT, TASK, NIL, EFFECT_CHANNEL_COUNT } from '../types.ts';
import type { System, Tick, ColonyId, Need, ColonyAIConfig } from '../types.ts';
import type { EntitySystem } from '../engine/EntitySystem.ts';
import type { WorldGrid } from '../engine/WorldGrid.ts';
import type { Rng } from '../rng.ts';
import type { ColonySystem } from './colony.ts';
import type { TaskQueue } from './taskQueue.ts';
import type { GeneInventory, GeneId } from './genes.ts';
import type { ColonyPersonality } from './personality.ts';
import type { HiveRead } from './hive.ts';
import { KIND } from '../types.ts';
import { senseNearestFood, senseNearestEnemy } from './sensing.ts';

/** Forage tasks an AI Food decision enqueues per cadence. */
const FORAGE_BATCH = 3;
/** Weight on each rival sighting when scoring Defense. */
const THREAT_WEIGHT = 3;
/** A need must clear this to win the arbiter (Reproduction is the fallback). */
const ACT_THRESHOLD = 1;
/**
 * Cap on the landmark expansion nudge (matches the ecosystem-pressure `biasCap`), so a
 * cluster of food hubs CANNOT dominate arbitration — it stays a threshold shift, not a
 * command. Folded into the raw expansion drive before the ≥0 clamp.
 */
const LANDMARK_EXPANSION_BIAS_CAP = 4;
/**
 * Fixed distance from the nest at which a Scout candidate site is proposed.
 * WORLD-SCALE: raised 5 -> 16 for the ~7.4x-larger default world so founded hive
 * nodes spread ACROSS the map instead of clustering at the nest — territory and
 * travel become real. The Scout->Expand relay still completes over this longer
 * distance because the scout is a Flyer (fast, wall-agnostic). This is an
 * expansion-REACH bump, not a sense-radius bump: the bug's perception stays small.
 */
const EXPAND_REACH = 16;
/**
 * Minimum population before a colony's emerged IDENTITY shapes its behaviour. A
 * struggling handful of bugs just fights to survive (identity-neutral, baseline
 * behaviour); a colony only affords a "character" once it is established. This
 * keeps identity from ever dooming a fragile colony — it expresses once a colony
 * has the numbers to back it, never as a death sentence for the weak. Emergence
 * itself is NOT gated (a colony still forms its identity from its whole life).
 */
const IDENTITY_MIN_POP = 12;

/**
 * What the AI reads from the ecosystem-pressure layer to ADAPT to nearby wildlife.
 * Decoupled to intent (a need bias + two booleans) so colonyAI never depends on the
 * pressure system's internals. Absent ⇒ the AI behaves byte-identically. The
 * concrete implementation is `EcosystemPressureSystem` (ecosystemPressure.ts).
 */
/** What the AI reads from the carrying-capacity foundation: a colony's dynamic ceiling. */
export interface CapacityRead {
  capacityOf(cid: ColonyId): number;
}

export interface EcosystemPressureRead {
  /** Small, capped need nudge from nearby wildlife (Defense/Food up, Expansion down). */
  biasFor(cid: ColonyId, need: Need): number;
  /** Predators near → keep foragers close (shorter, safer routes). */
  foragePulledIn(cid: ColonyId): boolean;
  /** Birds near → stop producing Flyers (they get picked off). */
  flyersSuppressed(cid: ColonyId): boolean;
}

export interface LandmarkBiasRead {
  /** Signed expansion desirability at a tile (food hubs +, barriers/predator-nests −). */
  expansionBiasAt(x: number, y: number): number;
}

export class ColonyAISystem implements System {
  readonly name = 'colony-ai';

  private readonly colonies: ColonySystem;
  private readonly entities: EntitySystem;
  private readonly grid: WorldGrid;
  private readonly rng: Rng;
  private readonly tasks: TaskQueue;
  private readonly genes: GeneInventory | undefined;
  private readonly config: ColonyAIConfig;
  private readonly maxPopulation: number;
  /**
   * Emergent-personality store (OPTIONAL). When wired, each decision (a) reads a
   * small bias from the colony's crystallized personality into its need scores
   * and (b) records the arbitrated winner back into the personality. Absent ⇒ the
   * AI behaves byte-identically to before this layer existed.
   */
  private readonly personality: ColonyPersonality | undefined;
  /**
   * Ecosystem-pressure reader (OPTIONAL, late-bound via `attachPressure` to break
   * the build cycle AI←pressure←wildlife←presence←ai). When wired, nearby wildlife
   * biases the colony's needs and reshapes foraging/expansion. Absent ⇒ no effect.
   */
  private pressure: EcosystemPressureRead | undefined;
  /**
   * Landmark expansion-desirability reader (OPTIONAL, late-bound via `attachLandmarks`).
   * When wired, the signed bias around a colony's nest shifts its Expansion threshold —
   * food hubs draw growth a little sooner, barriers/predator-nests hold it back (capped
   * to a small nudge). Absent ⇒ no effect, byte-identical to before this layer.
   */
  private landmarks: LandmarkBiasRead | undefined;
  /**
   * Carrying-capacity reader (OPTIONAL, late-bound via `attachCapacity`). When wired,
   * the Reproduction need targets the colony's dynamic capacity. Absent ⇒ the fixed
   * maxPopulation, so the AI is byte-identical to before this layer.
   */
  private capacity: CapacityRead | undefined;
  /**
   * Physical-hive reader (OPTIONAL, late-bound via `attachHive` to keep the one-way
   * hive→AI dependency from becoming a construction cycle). When wired, the colony
   * posts the hive-tending tasks (Nurse / Haul / Repair) against its real chambers.
   * Absent ⇒ none of those tasks are ever posted, so the AI is byte-identical to
   * before this layer (a world without a hive behaves as it always did).
   */
  private hive: HiveRead | undefined;

  readonly maxColonies: number;
  private readonly maxHiveNodes: number;

  // --- Per-colony SoA. ---
  /** Need vector: index [cid * NEED_COUNT + need]. */
  private readonly need: Int32Array;
  /** The winning NEED of the last decision (debug/hash/CLI). */
  private readonly priority: Int32Array;
  /** HIVE store: founded node coords, index [cid * maxHiveNodes + k]. */
  private readonly hiveX: Int32Array;
  private readonly hiveY: Int32Array;
  /** Hive nodes founded per colony. */
  private readonly hiveCount: Int32Array;
  /**
   * Per-colony count of CONSECUTIVE decisions on which the colony WANTED to expand
   * (Expansion won) but could not dispatch/complete a Scout (no Flyer was free to
   * relay the site). Reset to 0 whenever a Scout/Expand task is live or expansion
   * is not the winner. Once it reaches `scoutTimeout` the colony founds directly,
   * skipping the Scout step — the deterministic deadlock breaker.
   */
  private readonly scoutStallTicks: Int32Array;
  /** Caller-owned scratch for the per-decision gene-profile read (no per-call
   *  allocation): `profileOut` holds the colony's summed EFFECT_CHANNEL vector,
   *  `profileScratch` is reused per gene inside colonyGeneProfile. */
  private readonly profileOut: Int32Array;
  private readonly profileScratch: Int32Array;
  /** Caller-owned 2-int scratch for a chamber-centre read (no per-call allocation). */
  private readonly chamberScratch: Int32Array;

  // --- Public counters (deterministic; folded into the hash, read by tests). ---
  decisionsTotal = 0;
  hiveFoundedTotal = 0;
  splicesTotal = 0;
  /** Hive nodes founded via the Scout-timeout FALLBACK (a subset of hiveFoundedTotal). */
  scoutFallbacksTotal = 0;
  /** Nurse/Haul/Repair tasks the AI has enqueued (deterministic; folded into the hash). */
  nurseEnqueuedTotal = 0;
  haulEnqueuedTotal = 0;
  repairEnqueuedTotal = 0;

  /** Bound once so the Expand/Scout pursuit callbacks never allocate per tick. */
  readonly onExpand: (colony: ColonyId, x: number, y: number) => void;
  readonly onScout: (colony: ColonyId, x: number, y: number) => void;
  /** Bound once: the hive-tending pursuit callbacks (route arrivals into the hive). */
  readonly onNurse: (colony: ColonyId) => void;
  readonly onHaul: (colony: ColonyId) => void;
  readonly onRepair: (colony: ColonyId) => void;

  constructor(
    colonies: ColonySystem,
    entities: EntitySystem,
    grid: WorldGrid,
    rng: Rng,
    tasks: TaskQueue,
    genes: GeneInventory | undefined,
    config: ColonyAIConfig,
    maxPopulation: number,
    personality?: ColonyPersonality,
  ) {
    this.colonies = colonies;
    this.entities = entities;
    this.grid = grid;
    this.rng = rng;
    this.tasks = tasks;
    this.genes = genes;
    this.config = config;
    this.maxPopulation = maxPopulation;
    this.personality = personality;
    this.maxColonies = colonies.capacity;
    this.maxHiveNodes = config.maxHiveNodes;

    this.need = new Int32Array(this.maxColonies * NEED_COUNT);
    this.priority = new Int32Array(this.maxColonies).fill(NEED.Reproduction);
    this.hiveX = new Int32Array(this.maxColonies * this.maxHiveNodes);
    this.hiveY = new Int32Array(this.maxColonies * this.maxHiveNodes);
    this.hiveCount = new Int32Array(this.maxColonies);
    this.scoutStallTicks = new Int32Array(this.maxColonies);
    this.profileOut = new Int32Array(EFFECT_CHANNEL_COUNT);
    this.profileScratch = new Int32Array(EFFECT_CHANNEL_COUNT);
    this.chamberScratch = new Int32Array(2);

    this.onExpand = (colony, x, y) => this.foundHive(colony, x, y);
    this.onScout = (colony, x, y) => this.onScoutArrived(colony, x, y);
    // Hive-tending arrivals route into the hive bridge (no-op until attachHive).
    this.onNurse = (colony) => { if (this.hive !== undefined) this.hive.nurseTend(colony); };
    this.onHaul = (colony) => { if (this.hive !== undefined) this.hive.haulFromStorage(colony); };
    this.onRepair = (colony) => { if (this.hive !== undefined) this.hive.repairChamber(colony); };
  }

  /** Late-bind the ecosystem-pressure reader (see the field doc for the cycle). */
  attachPressure(pressure: EcosystemPressureRead): void {
    this.pressure = pressure;
  }

  /**
   * Late-bind the landmark expansion-desirability reader. Once wired, a colony's nest
   * surroundings shift its Expansion need (food hubs draw growth, barriers/predator-nests
   * hold it back). Optional ⇒ the AI is byte-identical to before this layer.
   */
  attachLandmarks(landmarks: LandmarkBiasRead): void {
    this.landmarks = landmarks;
  }

  /**
   * Late-bind the carrying-capacity reader so the AI's Reproduction need targets the
   * colony's DYNAMIC capacity instead of the fixed maxPopulation — it stops pushing
   * growth as the colony nears capacity (referencing the population-stability
   * foundation rather than its own limit). Optional ⇒ falls back to maxPopulation.
   */
  attachCapacity(capacity: CapacityRead): void {
    this.capacity = capacity;
  }

  /**
   * Late-bind the physical-hive bridge so the colony can post hive-tending tasks
   * (Nurse / Haul / Repair) against its real chambers and resolve them on arrival.
   * One-way (hive reads the AI's founded nodes; this lets the AI read the hive's
   * chambers back) — late-bound to avoid the construction cycle. Optional ⇒ the
   * three hive tasks are never posted, so behaviour is unchanged without it.
   */
  attachHive(hive: HiveRead): void {
    this.hive = hive;
  }

  // --- Per-tick: decide on each colony's stagger cadence ---------------------

  update(tick: Tick): void {
    const interval = this.config.decisionInterval;
    const count = this.colonies.count;
    for (let cid = 0; cid < count; cid++) {
      // Stagger: colony cid decides when tick % interval === cid % interval.
      if (tick % interval !== cid % interval) continue;
      this.decide(cid, tick);
    }
  }

  // --- The decision: analyze → arbitrate → act -------------------------------

  private decide(cid: ColonyId, tick: Tick): void {
    this.analyzeNeeds(cid);
    // Personality FEEDBACK (before arbitration): the colony's crystallized
    // temperament adds a SMALL, capped bias to its need scores, nudging marginal
    // calls without overriding survival. Decision N is biased by the personality
    // formed from decisions 1..N-1 (we record AFTER arbitrating), so there is no
    // same-tick circularity. Clamped at 0 so a bias can never drive a need
    // negative. No-op when the personality layer is unwired.
    // Identity only EXPRESSES once a colony is established (IDENTITY_MIN_POP); a
    // struggling colony stays identity-neutral and just fights to survive.
    const personality = this.personality;
    const identityActive =
      personality !== undefined && this.colonies.populationAt(cid) >= IDENTITY_MIN_POP;
    if (identityActive) {
      const base = cid * NEED_COUNT;
      for (let nd = 0; nd < NEED_COUNT; nd++) {
        const v = (this.need[base + nd] as number) + (personality as ColonyPersonality).biasFor(cid, nd as Need);
        this.need[base + nd] = v > 0 ? v : 0;
      }
    }
    // Ecosystem-pressure FEEDBACK (same shape as personality): nearby wildlife adds
    // a small, capped need bias — predators/raiders raise Defense, a robbed larder
    // raises Food, and danger lowers Expansion. Clamped ≥ 0. No-op when unwired.
    const pressure = this.pressure;
    if (pressure !== undefined) {
      const base = cid * NEED_COUNT;
      for (let nd = 0; nd < NEED_COUNT; nd++) {
        const v = (this.need[base + nd] as number) + pressure.biasFor(cid, nd as Need);
        this.need[base + nd] = v > 0 ? v : 0;
      }
    }
    const winner = this.arbitrate(cid);
    this.priority[cid] = winner;
    // The scout-stall timer counts CONSECUTIVE Expansion decisions that could not
    // relay a Scout, so it rests whenever Expansion is not the winner (only the
    // Expansion path in act() advances or clears it from there).
    if (winner !== NEED.Expansion) this.scoutStallTicks[cid] = 0;
    // Record the arbitrated choice into the personality (after the winner is set),
    // accumulating the trait tendencies that future decisions will read above.
    if (personality !== undefined) {
      personality.record(
        cid,
        winner,
        this.colonies.populationAt(cid),
        this.colonies.casteCountAt(cid, CASTE.Soldier),
        this.colonies.killsByAt(cid),
      );
      // STRUCTURAL signals: the colony's genes + claimed territory also shape its
      // identity, not just its decisions. Sampled on the same cadence.
      personality.recordStructural(
        cid,
        this.dominantGeneChannel(cid),
        this.hiveCount[cid] as number,
      );
    }
    this.act(cid, winner, tick);
    // Hive-tending maintenance (Nurse / Haul / Repair): condition-driven, posted on
    // the same decision cadence as the arbitrated tasks. No-op without a hive.
    this.enqueueHiveTasks(cid, winner);
    this.decisionsTotal++;
  }

  /** Fill the colony's NEED slice with integer scores. */
  private analyzeNeeds(cid: ColonyId): void {
    const cfg = this.config;
    const base = cid * NEED_COUNT;
    const pop = this.colonies.populationAt(cid);
    const food = this.colonies.foodAt(cid);

    // Food: rises as the store falls below lowFood relative to population.
    const foodNeed = pop + cfg.lowFood - food;
    this.need[base + NEED.Food] = foodNeed > 0 ? foodNeed : 0;

    // Defense: rival organisms scanned near the nest, minus soldiers on hand.
    const enemies = this.countThreats(cid);
    const soldiers = this.colonies.casteCountAt(cid, CASTE.Soldier);
    const defenseNeed = enemies * THREAT_WEIGHT - soldiers;
    this.need[base + NEED.Defense] = defenseNeed > 0 ? defenseNeed : 0;

    // Expansion: population beyond what the current territory supports; gated to
    // 0 once at the hive cap.
    const hives = this.hiveCount[cid] as number;
    let expandNeed = 0;
    if (hives < this.maxHiveNodes) {
      expandNeed = pop - cfg.expandPopThreshold * (hives + 1);
      // Landmark DRAW (opt-in): the nest's surroundings shift the expansion threshold —
      // nearby food hubs (positive desirability) pull growth a little sooner, barriers
      // and predator-nests (negative) hold it back. Capped to a small nudge and folded
      // into the RAW drive BEFORE the ≥0 clamp, so a colony nowhere near its threshold
      // is not pushed to expand by locale alone — it only modulates real drive.
      if (this.landmarks !== undefined) {
        let b = this.landmarks.expansionBiasAt(
          this.colonies.homeXAt(cid),
          this.colonies.homeYAt(cid),
        );
        if (b > LANDMARK_EXPANSION_BIAS_CAP) b = LANDMARK_EXPANSION_BIAS_CAP;
        else if (b < -LANDMARK_EXPANSION_BIAS_CAP) b = -LANDMARK_EXPANSION_BIAS_CAP;
        expandNeed += b;
      }
      if (expandNeed < 0) expandNeed = 0;
    }
    this.need[base + NEED.Expansion] = expandNeed;

    // Reproduction: headroom under the cap, scaled down when food is scarce.
    // Headroom under the colony's CARRYING CAPACITY (dynamic) when wired, else the
    // fixed cap — so the AI eases off growth as the colony approaches capacity.
    const ceiling = this.capacity ? this.capacity.capacityOf(cid) : this.maxPopulation;
    let reproNeed = ceiling - pop;
    if (reproNeed < 0) reproNeed = 0;
    if (food <= 0) reproNeed = 0; // can't grow with an empty larder
    this.need[base + NEED.Reproduction] = reproNeed;

    // Evolution: genes held beyond the minimum.
    let evoNeed = 0;
    if (this.genes !== undefined) {
      evoNeed = this.genes.countOf(cid) - cfg.evolutionMinGenes;
      if (evoNeed < 0) evoNeed = 0;
    }
    this.need[base + NEED.Evolution] = evoNeed;
  }

  /**
   * Pick the winning action by fixed priority Food > Defense > Expansion >
   * Evolution, taking the first whose score clears ACT_THRESHOLD; Reproduction
   * is the fallback resting/growth state.
   */
  private arbitrate(cid: ColonyId): Need {
    const base = cid * NEED_COUNT;
    if ((this.need[base + NEED.Food] as number) >= ACT_THRESHOLD) return NEED.Food;
    if ((this.need[base + NEED.Defense] as number) >= ACT_THRESHOLD) return NEED.Defense;
    if ((this.need[base + NEED.Expansion] as number) >= ACT_THRESHOLD) return NEED.Expansion;
    if ((this.need[base + NEED.Evolution] as number) >= ACT_THRESHOLD) return NEED.Evolution;
    return NEED.Reproduction;
  }

  /** Act on the winning need (self-correcting — recomputed next cadence). */
  private act(cid: ColonyId, winner: Need, _tick: Tick): void {
    const colonies = this.colonies;
    switch (winner) {
      case NEED.Food: {
        colonies.setSpawnBias(cid, CASTE.Worker);
        this.enqueueForage(cid);
        break;
      }
      case NEED.Defense: {
        colonies.setSpawnBias(cid, CASTE.Soldier);
        this.enqueueDefend(cid);
        break;
      }
      case NEED.Expansion: {
        // Flyer suppression: under bird/dragonfly territory, raising Flyers (the
        // Scout caste) just feeds the predators — hold ground with Workers and
        // don't push an aerial expansion until the skies clear. Expansion is paused,
        // so the scout-stall timer rests (the fallback never fires under suppression).
        if (this.pressure !== undefined && this.pressure.flyersSuppressed(cid)) {
          colonies.setSpawnBias(cid, CASTE.Worker);
          this.scoutStallTicks[cid] = 0;
        } else {
          colonies.setSpawnBias(cid, CASTE.Flyer);
          this.enqueueExpansion(cid);
        }
        break;
      }
      case NEED.Evolution: {
        this.evolve(cid);
        colonies.setSpawnBias(cid, -1);
        break;
      }
      default: {
        // Reproduction: rest + grow. An EMERGED colony's IDENTITY shapes idle
        // growth in two SAFE, additive ways (it never starves survival — a live
        // need is always arbitrated first, and this only runs in the resting state):
        //   • MILITARY DOCTRINE — top the standing garrison up to the identity's
        //     soldier ratio (martial identities keep a bigger army at peace);
        //   • RESOURCE ALLOCATION — once the garrison is met, nudge toward the
        //     identity's economic caste ONLY if the colony already fields a healthy
        //     guard (≥ a safety floor). Below that floor it falls back to the
        //     balanced default cycle (-1), so identity can tilt — never gut — the
        //     economy, and an economic colony still raises enough soldiers to
        //     defend, hunt and loot.
        let bias = -1;
        const p = this.personality;
        const pop = colonies.populationAt(cid);
        if (p !== undefined && pop >= IDENTITY_MIN_POP && p.hasEmerged(cid)) {
          const soldiers = colonies.casteCountAt(cid, CASTE.Soldier);
          // Below the identity's garrison ratio ⇒ raise Soldiers; at or above it ⇒
          // tilt toward the economic caste. Every identity's garrison is a healthy
          // floor (see SOLDIER_PCT), so even an economic colony keeps a real guard
          // and can still defend, hunt and loot.
          bias = (soldiers * 100 < pop * p.soldierDoctrine(cid))
            ? CASTE.Soldier
            : p.economicCaste(cid);
        }
        colonies.setSpawnBias(cid, bias);
        break;
      }
    }
  }

  // --- Action helpers --------------------------------------------------------

  /**
   * The EFFECT_CHANNEL the colony invests its genes in most (argmax of its summed
   * gene profile), or -1 if it has no positively-expressed genes. Drives the
   * gene→identity signal. Allocation-free — reuses the caller-owned scratch
   * buffers; the walk is O(genes-held) on the decision cadence, not per tick.
   */
  private dominantGeneChannel(cid: ColonyId): number {
    const genes = this.genes;
    if (genes === undefined) return -1;
    genes.colonyGeneProfile(cid, this.profileOut, this.profileScratch);
    let best = -1;
    let bestVal = 0;
    for (let c = 0; c < EFFECT_CHANNEL_COUNT; c++) {
      const v = this.profileOut[c] as number;
      if (v > bestVal) { bestVal = v; best = c; }
    }
    return best;
  }

  /** Count rival organisms within threatRadius of the nest (grid buckets). */
  private countThreats(cid: ColonyId): number {
    const grid = this.grid;
    const e = this.entities;
    const r = this.config.threatRadius;
    const hx = this.colonies.homeXAt(cid);
    const hy = this.colonies.homeYAt(cid);
    const minX = hx - r < 0 ? 0 : hx - r;
    const minY = hy - r < 0 ? 0 : hy - r;
    const maxX = hx + r >= grid.width ? grid.width - 1 : hx + r;
    const maxY = hy + r >= grid.height ? grid.height - 1 : hy + r;
    let count = 0;
    for (let ty = minY; ty <= maxY; ty++) {
      for (let tx = minX; tx <= maxX; tx++) {
        const ti = grid.index(tx, ty);
        for (let cur = grid.headAt(ti); cur !== NIL; cur = grid.next(cur)) {
          const c = cur as number;
          if ((e.kind[c] as number) !== KIND.Organism) continue;
          if ((e.colony[c] as number) === cid) continue;
          if ((e.hp[c] as number) <= 0) continue;
          count++;
        }
      }
    }
    return count;
  }

  /** Enqueue up to FORAGE_BATCH Forage tasks at food tiles sensed near the nest. */
  private enqueueForage(cid: ColonyId): void {
    const e = this.entities;
    const hx = this.colonies.homeXAt(cid);
    const hy = this.colonies.homeYAt(cid);
    // Wide sense radius from the nest so the colony can target real food nodes —
    // but pull foragers IN when predators lurk (the spider-nest route change): a
    // tighter radius keeps trips short and near the defended nest.
    let r = this.config.threatRadius + this.config.lowFood;
    if (this.pressure !== undefined && this.pressure.foragePulledIn(cid)) r = (r >> 1) + 1;
    for (let k = 0; k < FORAGE_BATCH; k++) {
      const food = senseNearestFood(e, this.grid, hx, hy, r);
      if (food === NIL) break;
      const fx = e.posX[food] as number;
      const fy = e.posY[food] as number;
      // Avoid enqueuing a duplicate task on the same tile this cadence.
      if (this.hasOpenTaskAt(cid, TASK.Forage, fx, fy)) break;
      if (this.tasks.enqueue(cid, TASK.Forage, fx, fy) === NIL) break;
    }
  }

  /** Enqueue a Defend task at the nearest sensed rival tile. */
  private enqueueDefend(cid: ColonyId): void {
    const e = this.entities;
    const hx = this.colonies.homeXAt(cid);
    const hy = this.colonies.homeYAt(cid);
    const enemy = senseNearestEnemy(e, this.grid, hx, hy, this.config.threatRadius, cid);
    if (enemy === NIL) return;
    const ex = e.posX[enemy] as number;
    const ey = e.posY[enemy] as number;
    if (this.hasOpenTaskAt(cid, TASK.Defend, ex, ey)) return;
    this.tasks.enqueue(cid, TASK.Defend, ex, ey);
  }

  /**
   * Drive autonomous expansion: if no Scout/Expand task is pending, propose a
   * candidate site a fixed distance from the nest in an RNG-chosen direction and
   * enqueue a Scout. A Scout completion promotes the site to an Expand (onScout);
   * an Expand completion founds the hive node (onExpand).
   *
   * SCOUT-TIMEOUT FALLBACK: a crowded-but-poor colony that cannot spare a Flyer
   * never relays a site, so expansion stalls (it self-corrects, never deadlocks,
   * but a node never gets founded). We count consecutive Expansion decisions on
   * which a Scout could not be dispatched/completed; once that reaches
   * `scoutTimeout` we found a node DIRECTLY at the nearest passable tile, skipping
   * the Scout step (via the SAME foundHive path the normal flow uses, so the hive
   * store + chambers realise identically). The timer resets whenever a Scout/Expand
   * IS in flight, so a Flyer-healthy colony never hits the fallback.
   */
  private enqueueExpansion(cid: ColonyId): void {
    if ((this.hiveCount[cid] as number) >= this.maxHiveNodes) {
      this.scoutStallTicks[cid] = 0;
      return;
    }
    // A Scout/Expand already in flight ⇒ the relay is working; reset the stall.
    if (this.hasOpenTaskKind(cid, TASK.Scout) || this.hasOpenTaskKind(cid, TASK.Expand)) {
      this.scoutStallTicks[cid] = 0;
      return;
    }
    const hx = this.colonies.homeXAt(cid);
    const hy = this.colonies.homeYAt(cid);
    // Eight cardinal/diagonal directions; pick one deterministically.
    const dir = this.rng.int(8);
    const dx = DIR8X[dir] as number;
    const dy = DIR8Y[dir] as number;
    let sx = hx + dx * EXPAND_REACH;
    let sy = hy + dy * EXPAND_REACH;
    sx = sx < 0 ? 0 : sx >= this.grid.width ? this.grid.width - 1 : sx;
    sy = sy < 0 ? 0 : sy >= this.grid.height ? this.grid.height - 1 : sy;
    // Can we actually staff a Scout right now? (a free, untasked Flyer). If not, the
    // relay cannot start this cadence — count it toward the stall. If the proposed
    // site is impassable we likewise made no progress, so that also counts.
    const canScout = this.grid.isPassable(sx, sy) && this.hasIdleScout(cid);
    if (canScout) {
      this.scoutStallTicks[cid] = 0;
      this.tasks.enqueue(cid, TASK.Scout, sx, sy);
      return;
    }
    // No Scout could be relayed this cadence — advance the stall timer. On timeout,
    // found directly at the nearest passable tile (deterministic ring scan).
    const stall = (this.scoutStallTicks[cid] as number) + 1;
    this.scoutStallTicks[cid] = stall;
    if (stall >= this.config.scoutTimeout) {
      this.scoutStallTicks[cid] = 0;
      const found = this.nearestExpansionSite(cid, hx, hy);
      if (found) {
        this.foundHive(cid, this.chamberScratch[0] as number, this.chamberScratch[1] as number);
        this.scoutFallbacksTotal++;
      }
    }
  }

  /** True if the colony has at least one IDLE, untasked Flyer to relay a Scout. */
  private hasIdleScout(cid: ColonyId): boolean {
    const e = this.entities;
    const active = e.activeView();
    const n = e.aliveCount;
    const tasks = this.tasks;
    for (let i = 0; i < n; i++) {
      const id = active[i] as number;
      if ((e.kind[id] as number) !== KIND.Organism) continue;
      if ((e.colony[id] as number) !== cid) continue;
      if ((e.caste[id] as number) !== CASTE.Flyer) continue;
      if (tasks.hasTask(id)) continue;
      return true;
    }
    return false;
  }

  /**
   * Find the NEAREST passable tile to the nest that is not already a founded hive
   * node, writing it to chamberScratch[0]/[1]; returns true if one was found. A
   * deterministic outward RING scan: rings r = 1..searchSpan, and within a ring the
   * perimeter is visited in fixed (y,x) row-major order, so the FIRST passable,
   * non-duplicate tile is a stable tie-break ("nearest" by Chebyshev, then top-left).
   * Allocation-free; bounded by the search span. Used only by the Scout-timeout
   * fallback (a rare, off-cadence event), never the per-tick hot path.
   */
  private nearestExpansionSite(cid: ColonyId, hx: number, hy: number): boolean {
    const grid = this.grid;
    const span = EXPAND_REACH; // search out to the normal scout reach
    for (let r = 1; r <= span; r++) {
      const minX = hx - r;
      const maxX = hx + r;
      const minY = hy - r;
      const maxY = hy + r;
      for (let ty = minY; ty <= maxY; ty++) {
        if (ty < 0 || ty >= grid.height) continue;
        for (let tx = minX; tx <= maxX; tx++) {
          if (tx < 0 || tx >= grid.width) continue;
          // Perimeter of this ring only (Chebyshev distance exactly r).
          if (tx !== minX && tx !== maxX && ty !== minY && ty !== maxY) continue;
          if (!grid.isPassable(tx, ty)) continue;
          if (this.isFoundedNode(cid, tx, ty)) continue;
          this.chamberScratch[0] = tx;
          this.chamberScratch[1] = ty;
          return true;
        }
      }
    }
    return false;
  }

  /** True if (x,y) is already a founded hive node of `cid` (avoid duplicate founds). */
  private isFoundedNode(cid: ColonyId, x: number, y: number): boolean {
    const k = this.hiveCount[cid] as number;
    const base = cid * this.maxHiveNodes;
    for (let i = 0; i < k; i++) {
      if ((this.hiveX[base + i] as number) === x && (this.hiveY[base + i] as number) === y) return true;
    }
    return false;
  }

  /**
   * Post the hive-tending tasks (Nurse / Haul / Repair) keyed off the colony's real
   * chambers + state. Each is gated to fire ONLY when its condition holds and no
   * task of its kind is already open (de-duped, one per cadence), so the cost is
   * bounded and the queue never piles identical work. No-op without a hive (the
   * three tasks are simply never posted, preserving pre-hive behaviour). Targets
   * route the tending Worker to the right tile so the arrival callback fires there.
   *
   * Triggers (all deterministic, on the decision cadence):
   *   • REPAIR — a non-core chamber has decayed to/below repairUrgency. Target: that
   *     chamber's centre. (The most-degraded room; the AI and effect agree on it.)
   *   • HAUL   — the colony holds a Storage chamber AND its nest food has fallen
   *     to/below haulNestThreshold (relative to population). Target: the nest, where
   *     the Haul releases Storage reserve into the store.
   *   • NURSE  — the colony holds a Nursery AND wants to reproduce (the resting
   *     Reproduction state, or a positive Reproduction need). Target: the Nursery.
   */
  private enqueueHiveTasks(cid: ColonyId, winner: Need): void {
    const hive = this.hive;
    if (hive === undefined) return;
    const scratch = this.chamberScratch;

    // REPAIR: restore the most-degraded chamber (skips the never-wearing core).
    if (!this.hasOpenTaskKind(cid, TASK.Repair)) {
      const k = hive.mostDegradedChamber(cid);
      if (k >= 0 && hive.chamberCentreAt(cid, k, scratch)) {
        if (this.tasks.enqueue(cid, TASK.Repair, scratch[0] as number, scratch[1] as number) !== NIL) {
          this.repairEnqueuedTotal++;
        }
      }
    }

    // HAUL: ease a hungry nest from Storage reserve (only if a Storage room exists).
    const pop = this.colonies.populationAt(cid);
    if (
      hive.hasChamber(cid, CHAMBER.Storage) &&
      this.colonies.foodAt(cid) <= pop + this.config.haulNestThreshold &&
      !this.hasOpenTaskKind(cid, TASK.Haul)
    ) {
      const hx = this.colonies.homeXAt(cid);
      const hy = this.colonies.homeYAt(cid);
      if (this.tasks.enqueue(cid, TASK.Haul, hx, hy) !== NIL) this.haulEnqueuedTotal++;
    }

    // NURSE: tend the Nursery while the colony wants to grow (resting/repro state).
    const wantsRepro = winner === NEED.Reproduction || (this.need[cid * NEED_COUNT + NEED.Reproduction] as number) > 0;
    if (
      wantsRepro &&
      hive.hasChamber(cid, CHAMBER.Nursery) &&
      !this.hasOpenTaskKind(cid, TASK.Nurse) &&
      hive.chamberCentreOfType(cid, CHAMBER.Nursery, scratch)
    ) {
      if (this.tasks.enqueue(cid, TASK.Nurse, scratch[0] as number, scratch[1] as number) !== NIL) {
        this.nurseEnqueuedTotal++;
      }
    }
  }

  /** Scout arrived at a candidate site → promote it to an Expand task. */
  private onScoutArrived(cid: ColonyId, x: number, y: number): void {
    if ((this.hiveCount[cid] as number) >= this.maxHiveNodes) return;
    if (this.hasOpenTaskKind(cid, TASK.Expand)) return;
    this.tasks.enqueue(cid, TASK.Expand, x, y);
  }

  /** Expand arrived → FOUND a hive node, pay the colony its growth dividend. */
  private foundHive(cid: ColonyId, x: number, y: number): void {
    const k = this.hiveCount[cid] as number;
    if (k >= this.maxHiveNodes) return;
    this.hiveX[cid * this.maxHiveNodes + k] = x;
    this.hiveY[cid * this.maxHiveNodes + k] = y;
    this.hiveCount[cid] = k + 1;
    this.colonies.depositFood(cid, this.config.hiveDividend);
    this.hiveFoundedTotal++;
  }

  /** Autonomous evolution: splice the colony's two most-recent genes. */
  private evolve(cid: ColonyId): void {
    const genes = this.genes;
    if (genes === undefined) return;
    if (genes.countOf(cid) < 2) return;
    const a: GeneId = genes.firstOf(cid);
    if (a === NIL) return;
    const b: GeneId = genes.nextOf(a);
    if (b === NIL) return;
    if (genes.spliceGenes(a, b) !== NIL) this.splicesTotal++;
  }

  // --- Open-task de-dupe helpers (avoid piling identical tasks per cadence) ---

  private hasOpenTaskAt(cid: ColonyId, kind: number, x: number, y: number): boolean {
    const tasks = this.tasks;
    for (let t = tasks.firstOf(cid); t !== NIL; t = tasks.nextOf(t)) {
      if (tasks.taskKindOf(t) !== kind) continue;
      if (tasks.taskTargetXOf(t) === x && tasks.taskTargetYOf(t) === y) return true;
    }
    return false;
  }

  private hasOpenTaskKind(cid: ColonyId, kind: number): boolean {
    const tasks = this.tasks;
    for (let t = tasks.firstOf(cid); t !== NIL; t = tasks.nextOf(t)) {
      if (tasks.taskKindOf(t) === kind) return true;
    }
    return false;
  }

  // --- Accessors (hash / CLI / tests) ----------------------------------------

  needAt(cid: ColonyId, need: Need): number {
    return this.need[cid * NEED_COUNT + need] as number;
  }
  priorityAt(cid: ColonyId): number {
    return this.priority[cid] as number;
  }
  hiveCountAt(cid: ColonyId): number {
    return this.hiveCount[cid] as number;
  }
  hiveXAt(cid: ColonyId, k: number): number {
    return this.hiveX[cid * this.maxHiveNodes + k] as number;
  }
  hiveYAt(cid: ColonyId, k: number): number {
    return this.hiveY[cid * this.maxHiveNodes + k] as number;
  }
}

// 8-direction unit deltas (cardinal + diagonal) for expansion proposals.
const DIR8X: readonly number[] = [0, 1, 1, 1, 0, -1, -1, -1];
const DIR8Y: readonly number[] = [-1, -1, 0, 1, 1, 1, 0, -1];
